import React, { useState } from "react";
import { Building2, HeartHandshake, Landmark, Scale, FileText, Compass, ArrowRight } from "lucide-react";
import CtaSection, { CtaButton } from "../components/common/CtaSection";
import LeadCaptureModal from "../components/common/LeadCaptureModal";
import { LEAD_CHANNELS } from "../constants/data";

const GOLD = "#50C878";
const NAVY = "#013220";

// ─── OUR SERVICES ─────────────────────────────────────────────
// Editable service cards. Each card has its own CTA, and there's a
// closing CTA band — keeping the page contact-first throughout.
// ─────────────────────────────────────────────────────────────

const SERVICES = [
  { icon: HeartHandshake, title: "Group Negotiation", body: "The heart of what we do. We pool buyer demand and negotiate pricing and terms with builders as a collective." },
  { icon: Building2, title: "Curated Property Discovery", body: "Hand-picked, RERA-verified apartments, villas and plots across Hyderabad — matched to your budget and area." },
  { icon: Landmark, title: "Home Loan Assistance", body: "Introductions to leading banking partners, plus help comparing offers and preparing documentation." },
  { icon: Scale, title: "Legal & RERA Verification", body: "We verify titles and RERA registration before listing, and connect you with legal advisors when needed." },
  { icon: FileText, title: "Paperwork & Registration", body: "Guidance through agreements, payments and final registration so nothing slips through the cracks." },
  { icon: Compass, title: "Site Visits & Advisory", body: "Coordinated site visits and honest, buyer-side advice — we work for you, not the builder." },
];

export default function ServicesPage() {
  const [showModal, setShowModal] = useState(false);
  const [serviceContext, setServiceContext] = useState("");

  function enquire(service) {
    setServiceContext(service);
    setShowModal(true);
  }

  return (
    <div style={{ background: "#F2F8F3", color: NAVY, minHeight: "100vh", paddingTop: "68px" }}>
      {/* Hero */}
      <div style={{ padding: "64px 24px 44px", textAlign: "center", background: "linear-gradient(135deg, rgba(80,200,120,0.10), rgba(127,217,160,0.04))", borderBottom: "1px solid rgba(80,200,120,0.2)" }}>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase" }}>
          What We Offer
        </span>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.2rem, 5vw, 3.4rem)", fontWeight: 600, margin: "12px 0 16px" }}>
          Our Services
        </h1>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "1.05rem", color: "rgba(1,50,32,0.7)", lineHeight: 1.7, maxWidth: "680px", margin: "0 auto 28px" }}>
          From the first conversation to the keys in your hand, we support every step of your home-buying journey — with the buying power of a group behind you.
        </p>
        <CtaButton label="Talk to an Advisor" onClick={() => enquire("General services enquiry")} />
      </div>

      {/* Services grid */}
      <section style={{ maxWidth: "1100px", margin: "0 auto", padding: "60px 24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }}>
          {SERVICES.map(({ icon: Icon, title, body }) => (
            <div key={title} style={{ display: "flex", flexDirection: "column", background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)", border: "1px solid rgba(80,200,120,0.18)", borderRadius: "16px", padding: "28px" }}>
              <div style={{ width: "48px", height: "48px", borderRadius: "10px", background: "rgba(80,200,120,0.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "18px" }}>
                <Icon size={24} color={GOLD} />
              </div>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.5rem", fontWeight: 600, margin: "0 0 10px" }}>{title}</h3>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.94rem", color: "rgba(1,50,32,0.74)", lineHeight: 1.7, margin: "0 0 20px", flex: 1 }}>{body}</p>
              <button
                onClick={() => enquire(title)}
                style={{ display: "inline-flex", alignItems: "center", gap: "6px", alignSelf: "flex-start", background: "transparent", border: `1px solid ${GOLD}`, borderRadius: "6px", padding: "9px 18px", color: GOLD, fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", transition: "background 0.2s" }}
                onMouseOver={(e) => (e.currentTarget.style.background = "rgba(80,200,120,0.10)")}
                onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
              >
                Enquire <ArrowRight size={14} />
              </button>
            </div>
          ))}
        </div>
      </section>

      <CtaSection
        eyebrow="Let's talk"
        title="Not sure where to start?"
        subtitle="Tell us what you're looking for and we'll recommend the right next step — at no cost."
        primaryLabel="Get in Touch"
        onPrimaryClick={() => enquire("General services enquiry")}
      />

      <LeadCaptureModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        channel={LEAD_CHANNELS.WEB}
        prefillMessage={serviceContext ? `Service enquiry: ${serviceContext}` : "Services enquiry"}
      />
    </div>
  );
}
